import api from './api';

export interface PendingAction {
  id: string;
  toolName: string;
  arguments: Record<string, any>;
  summary: string;
  status: 'pending' | 'confirmed' | 'rejected' | 'executed' | 'failed';
  result?: any;
  error?: string;
}

export interface AssistantMessage {
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  pendingActionIds?: string[];
  createdAt?: string;
}

export interface ChatResponse {
  conversationId: string;
  reply: string;
  pendingActions: PendingAction[];
  messages: AssistantMessage[];
}

export const getAssistantStatus = async (agencySlug: string) => {
  const res = await api.get(`/agencies/${agencySlug}/assistant/status`);
  return res.data as {
    enabled: boolean;
    feature: string;
    chat: { allowed: boolean; used: number; limit: number };
    tts: { allowed: boolean; used: number; limit: number };
  };
};

/** Browser IANA timezone, e.g. "America/New_York", "Australia/Melbourne". */
export function getBrowserTimeZone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && typeof tz === 'string') return tz;
  } catch {
    // ignore
  }
  return 'UTC';
}

export const sendAssistantChat = async (
  agencySlug: string,
  payload: { propertyId: string; message: string; conversationId?: string }
): Promise<ChatResponse> => {
  const res = await api.post(`/agencies/${agencySlug}/assistant/chat`, {
    ...payload,
    timeZone: getBrowserTimeZone(),
  });
  return res.data;
};

export const confirmAssistantAction = async (
  agencySlug: string,
  payload: { conversationId: string; actionId: string; confirm: boolean }
) => {
  const res = await api.post(`/agencies/${agencySlug}/assistant/actions/confirm`, {
    ...payload,
    timeZone: getBrowserTimeZone(),
  });
  return res.data as { action: PendingAction; conversationId: string; message?: string };
};

export const listAssistantConversations = async (agencySlug: string, propertyId: string) => {
  const res = await api.get(`/agencies/${agencySlug}/assistant/conversations`, {
    params: { propertyId },
  });
  return res.data;
};

export const getAssistantConversation = async (agencySlug: string, conversationId: string) => {
  const res = await api.get(`/agencies/${agencySlug}/assistant/conversations/${conversationId}`);
  return res.data;
};
